import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { CONTROL_IDS, getGroupedControlIds, searchControlIds } from '../../data/controlIds';

/**
 * ControlIdPicker - Smart dropdown for selecting E8 Control IDs
 */
const ControlIdPicker = ({ value, onChange, error, placeholder = "Select or search for a control ID..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredControls, setFilteredControls] = useState([]);
  const [groupedControls, setGroupedControls] = useState({});
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Initialize grouped controls
  useEffect(() => {
    setGroupedControls(getGroupedControlIds());
  }, []);

  // Filter controls based on search
  useEffect(() => {
    if (searchText.trim()) {
      setFilteredControls(searchControlIds(searchText));
    } else {
      setFilteredControls([]);
    }
  }, [searchText]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search when dropdown opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSelect = (controlId) => {
    onChange(controlId);
    setIsOpen(false);
    setSearchText('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  const displayValue = value ? CONTROL_IDS[value] || value : '';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Main Input */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 border rounded-md shadow-sm cursor-pointer flex items-center justify-between min-h-[38px] ${
          error ? 'border-red-300' : isOpen ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-300 hover:border-gray-400'
        } ${!value ? 'text-gray-500' : 'text-gray-900'}`}
      >
        <div className="flex-1 truncate">
          {value ? (
            <div className="flex items-center">
              <span className="font-mono text-sm text-blue-600 mr-2">{value}</span>
              <span className="text-sm text-gray-600 truncate">{CONTROL_IDS[value]?.replace(value + ' - ', '') || ''}</span>
            </div>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center space-x-1">
          {value && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-hidden">
          {/* Search Box */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search control IDs..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Results */}
          <div className="max-h-80 overflow-y-auto">
            {searchText.trim() ? (
              /* Search Results */
              <div className="p-2">
                {filteredControls.length > 0 ? (
                  <>
                    <div className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Search Results ({filteredControls.length})
                    </div>
                    {filteredControls.map(({ id, description }) => (
                      <button
                        key={id}
                        onClick={() => handleSelect(id)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded-md group"
                      >
                        <div className="font-mono text-sm text-blue-600 group-hover:text-blue-700">{id}</div>
                        <div className="text-sm text-gray-600 group-hover:text-gray-700 truncate">
                          {description.replace(id + ' - ', '')}
                        </div>
                      </button>
                    ))}
                  </>
                ) : (
                  <div className="px-3 py-8 text-center text-gray-500">
                    <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No controls found for "{searchText}"</p>
                    <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
                  </div>
                )}
              </div>
            ) : (
              /* Grouped Categories */
              <div className="p-2">
                {Object.entries(groupedControls).map(([category, maturityLevels]) => (
                  <div key={category} className="mb-4">
                    <div className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100 mb-2">
                      {category}
                    </div>
                    {Object.entries(maturityLevels)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([maturity, controls]) => (
                        <div key={maturity} className="mb-3">
                          <div className="px-2 py-1 text-xs text-gray-600 font-medium">{maturity}</div>
                          {controls.map(({ id, description }) => (
                            <button
                              key={id}
                              onClick={() => handleSelect(id)}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded-md group ml-2"
                            >
                              <div className="font-mono text-sm text-blue-600 group-hover:text-blue-700">{id}</div>
                              <div className="text-sm text-gray-600 group-hover:text-gray-700 truncate">
                                {description.replace(id + ' - ', '')}
                              </div>
                            </button>
                          ))}
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="border-t border-gray-200 p-3 bg-gray-50">
            <div className="text-xs text-gray-500">
              💡 <strong>Tip:</strong> Start typing to search, or browse by category above
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlIdPicker;